import React from 'react';
import { t, currentLanguage } from '../i18n';

// Shared constants + small helpers for the Vendor Ledger and the compliance
// (insurance / license expiry) tracking that gates vendor payments.

// How a payment went out. Mirrors the "Mode of Payment" column in the old
// spreadsheet. Admin-only, so these stay English.
export const PAYMENT_MODES = [
  'Cash', 'Check', 'Zelle', 'Venmo', 'PayPal', 'Cash App',
  'ACH / Bank Transfer', 'Credit Card', 'Other'
];

// The three documents a tech must keep current to stay payable, plus a
// catch-all. Values must stay in sync with server/compliance.js; labels are
// translated at render time via lg.type.*
export const CREDENTIAL_TYPES = ['insurance', 'business-license', 'occupational-license', 'other'];

export const credentialLabel = (type) => t('lg.type.' + type, type);

export const LEDGER_STATUSES = ['open', 'in-progress', 'completed', 'paid'];

// The server sends amountPaid/balance as virtuals, but recompute locally so the
// UI is still right if an entry is updated optimistically.
export const paidTotal = (entry) =>
  (entry?.payments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

export const balanceOf = (entry) =>
  (Number(entry?.invoiceAmount) || 0) - paidTotal(entry);

// Ledger-wide totals — the "Total Amount Paid" / "Total Amount Owed" cells
// from the spreadsheet.
export const ledgerTotals = (entries = []) => ({
  invoiced: entries.reduce((s, e) => s + (Number(e.invoiceAmount) || 0), 0),
  paid: entries.reduce((s, e) => s + paidTotal(e), 0),
  owed: entries.reduce((s, e) => s + Math.max(0, balanceOf(e)), 0),
});

// "in 43 days" / "43 days ago" / "today", in the viewer's language.
// Intl.RelativeTimeFormat handles every language's plural rules and word order
// for us, so there's nothing to translate by hand here.
export const expiryPhrase = (daysLeft) => {
  if (daysLeft == null) return '';
  try {
    return new Intl.RelativeTimeFormat(currentLanguage(), { numeric: 'auto' }).format(daysLeft, 'day');
  } catch {
    return daysLeft < 0 ? `${Math.abs(daysLeft)} days ago` : `in ${daysLeft} days`;
  }
};

// A document's display name: the three required types translate; a free-form
// "other" document keeps whatever the tech named it.
export const displayLabel = (item) =>
  item.type === 'other' ? (item.label || t('lg.type.other')) : credentialLabel(item.type);

// Colored pill for one document's state.
export const credentialTag = (item) => {
  const cls = { valid: 'tag-green', expiring: 'tag-amber', expired: 'tag-red', missing: 'tag-red' }[item.status] || 'tag-slate';
  const text = item.status === 'missing' ? t('lg.notOnFile') : expiryPhrase(item.daysLeft);
  return <span className={`tag ${cls}`}>{displayLabel(item)}: {text}</span>;
};

// Colored pill for a ledger row's state.
export const ledgerStatusTag = (status) => {
  const cls = {
    'open': 'tag-slate',
    'in-progress': 'tag-blue',
    'completed': 'tag-teal',
    'paid': 'tag-green',
  }[status] || 'tag-slate';
  return <span className={`tag ${cls}`}>{t('lg.st.' + status, status)}</span>;
};
